import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import { Link, NavLink, matchPath } from 'react-router-dom';
import Routes from 'Routes';
import { UserSessionContext } from 'components/UserSession';
import BookmarksModal from 'components/modals/bookmarks/BookmarksModal';
import {
  ENABLED_FEATURES,
  PAGE_NAMES,
  BRAND_COLOR,
  APP,
} from 'appenv';
import SearchField from './SearchField';
import UserAccountView from './UserAccountView';
import HelpButton from './HelpButton';
import BookmarksButton from './BookmarksButton';
import { BookmarksContext } from '../modals/bookmarks/Bookmarks';

const RootContainer = styled.div`
  width: 100%;
  z-index: 1000;
  position: relative;
`;

const useAppBarStyles = makeStyles(() => ({
  root: {
    zIndex: 1000,
    maxHeight: '64px',
  },
  colorPrimary: {
    color: '#484848',
    backgroundColor: '#FFF',
  },
}));

const StyledToolbar = withStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}))(Toolbar);

const StyledLogoContainer = styled.div`
  flex: 0 0 auto;
  width: 232px;
`;

const StyledLinksContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledActionsContainer = styled.div`
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  margin-left: ${({ hideUser }) => (hideUser ? 200 : 0)}px;
`;

const StyledLink = styled(Link)`
  display: inline-flex;
`;

const StyledNavLink = styled(NavLink)`
  flex: 0 1 auto;
  color: #484848;
  text-decoration: none;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 18px;
  padding: 6px 24px;
`;

const activeLinkStyle = {
  color: BRAND_COLOR || '#0CA58B',
};

const StyledLogo = styled.img`
  display: inline;
  align-self: center;
  height: ${({ logoHeight }) => logoHeight}px;
  vertical-align: middle;

  & :not(:last-child) {
    margin-right: 24px;
  }
`;

export default function DesktopAppBar({
  hideSearchBar,
  hideUser,
  logos,
  hideHelp,
  bookmarksModalOpen,
  openBookmarks,
  closeBookmarks,
}) {
  const appBarClasses = useAppBarStyles();

  const { canSee, firstVisibleRoute, userSession } = useContext(UserSessionContext);

  const logoHeight = useMemo(() => {
    switch (APP) {
      case 'polyu618':
        return 48;
      case 'ms624':
        return 30;
      case 'scmp711':
        return 40;
      case 'hkstp608':
        return 56;
      case 'lighthouse710':
      default:
        return 40;
    }
  }, []);

  const WebinarLink = (() => {
    switch (APP) {
      case 'ms624': return (
        <StyledNavLink to="/about" activeStyle={activeLinkStyle}>
          {PAGE_NAMES.webinar}
        </StyledNavLink>
      );
      default: return (
        <StyledNavLink to={Routes.webinar} activeStyle={activeLinkStyle}>
          {PAGE_NAMES.webinar}
        </StyledNavLink>
      );
    }
  })();

  return (
    <>
      <RootContainer>
        <AppBar
          position="static"
          className={`${appBarClasses.root} ${appBarClasses.colorPrimary}`}
          elevation={3}
        >
          <StyledToolbar>
            <StyledLogoContainer>
              <StyledLink to={Routes[firstVisibleRoute]}>
                {logos.map((logoUrl) => <StyledLogo logoHeight={logoHeight} src={logoUrl} alt="Event Logo" key={`image-${logoUrl}`} />)}
              </StyledLink>
            </StyledLogoContainer>
            <StyledLinksContainer>
              {
                canSee('lobby') && (
                  <StyledNavLink
                    to={Routes.lobby}
                    activeStyle={activeLinkStyle}
                    isActive={(match, location) => (
                      matchPath(location.pathname, {
                        path: [Routes.lobby, Routes.root],
                        exact: true,
                      })
                    )}
                  >
                    {PAGE_NAMES.lobby}
                  </StyledNavLink>
                )
              }
              {
                canSee('hall') && (
                  <StyledNavLink
                    to={Routes.hall}
                    activeStyle={activeLinkStyle}
                    isActive={(match, location) => (
                      matchPath(location.pathname, {
                        path: [Routes.hall, Routes.booth()],
                        exact: true,
                      })
                    )}
                  >
                    {PAGE_NAMES.hall}
                  </StyledNavLink>
                )
              }
              {
                canSee('webinar') && WebinarLink
              }
              {
                canSee('participant') && (
                  <StyledNavLink to={Routes.participant} activeStyle={activeLinkStyle}>
                    {PAGE_NAMES.participant}
                  </StyledNavLink>
                )
              }
            </StyledLinksContainer>
            <StyledActionsContainer hideUser={hideUser}>
              <BookmarksContext.Consumer>
                {({ bookmarkedAttachments }) => (
                  (ENABLED_FEATURES.bookmarks && Object.keys(bookmarkedAttachments).length !== 0) && <BookmarksButton openBookmarks={openBookmarks} />
                )}
              </BookmarksContext.Consumer>
              <BookmarksModal open={bookmarksModalOpen} onClose={closeBookmarks} />
              {!hideSearchBar && canSee('search') && <SearchField />}
              {!hideHelp && <HelpButton />}
              {!hideUser && userSession && <UserAccountView />}
            </StyledActionsContainer>
          </StyledToolbar>
        </AppBar>
      </RootContainer>
    </>
  );
}

DesktopAppBar.propTypes = {
  hideSearchBar: PropTypes.bool,
  hideUser: PropTypes.bool,
  logos: PropTypes.arrayOf(PropTypes.string).isRequired,
  hideHelp: PropTypes.bool,
  bookmarksModalOpen: PropTypes.bool.isRequired,
  openBookmarks: PropTypes.func.isRequired,
  closeBookmarks: PropTypes.func.isRequired,
};

DesktopAppBar.defaultProps = {
  hideSearchBar: false,
  hideUser: false,
  hideHelp: false,
};
